import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthToken, useAuthActions } from '@convex-dev/auth/react';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { API } from '../lib/api';

const AuthCallback = () => {
  const location = useLocation();
  const convexToken = useAuthToken();
  const { signOut } = useAuthActions();
  const { captureReferralCode, refreshFirstTouchReferral, consumeFirstTouchReferral } = useAuth();
  const [status, setStatus] = useState('Completing sign in...');
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const redirected = useRef(false);

  // Query for session token - this will return data when user is authenticated
  const sessionData = useQuery(
    api.auth.getSessionToken,
    convexToken ? {} : "skip"
  );

  useEffect(() => {
    const referralCodeFromQuery = new URLSearchParams(location.search).get('ref');
    if (referralCodeFromQuery) {
      captureReferralCode(referralCodeFromQuery);
      return;
    }

    // Keep the pending first-touch referral hydrated across the OAuth round-trip.
    refreshFirstTouchReferral();
  }, [captureReferralCode, location.search, refreshFirstTouchReferral]);

  useEffect(() => {
    // Prevent double-redirect
    if (redirected.current) return;

    const processAuth = async () => {
      console.log('AuthCallback: Processing auth, attempt:', attempts);
      console.log('AuthCallback: Convex token:', convexToken ? 'present' : 'not present');
      console.log('AuthCallback: Session data:', sessionData);

      // Check if we already have a custom token in localStorage
      const storedToken = localStorage.getItem('convex_token');
      if (storedToken) {
        console.log('AuthCallback: Already have token, redirecting to dashboard');
        redirected.current = true;
        // Use hard redirect to avoid race with ConvexAuthSync unmounting us
        window.location.replace('/dashboard');
        return;
      }

      if (sessionData && sessionData.token) {
        // Got the session token from Convex
        localStorage.setItem('convex_token', sessionData.token);
        const pendingReferral = refreshFirstTouchReferral();
        if (pendingReferral?.code && !pendingReferral.isConsumed) {
          try {
            await axios.post(
              `${API}/auth/attach-referral`,
              {
                referral_code: pendingReferral.code,
                attribution_source: 'google_oauth',
              },
              {
                headers: { Authorization: `Bearer ${sessionData.token}` },
              }
            );
            consumeFirstTouchReferral();
          } catch (attachError) {
            console.error('AuthCallback: Failed to attach referral', attachError);
          }
        }
        console.log('AuthCallback: Session created, redirecting to dashboard');
        redirected.current = true;
        // Send onboarding email for new Google OAuth users (fire-and-forget)
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL || '';
          fetch(API_URL + '/api/auth/send-onboarding', {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + sessionData.token }
          }).catch(function() {});
        } catch(e) {}
        // Use hard redirect to ensure it works even if ConvexAuthSync unmounts us
        window.location.replace('/dashboard');
        return;
      }

      if (sessionData === null && convexToken && attempts < 10) {
        // Session data query returned null, retry
        setStatus('Setting up your account...');
        setAttempts(prev => prev + 1);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else if (!convexToken && attempts >= 5) {
        setError('Authentication timed out. Please try again.');
      } else if (convexToken) {
        setStatus('Syncing your account...');
      } else {
        setStatus('Waiting for authentication...');
        if (attempts < 10) {
          setAttempts(prev => prev + 1);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    };

    processAuth();
  }, [convexToken, sessionData, attempts, consumeFirstTouchReferral, refreshFirstTouchReferral]);

  const handleBackToLogin = async () => {
    try {
      await signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    }
    localStorage.removeItem('convex_token');
    window.location.replace('/login');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={handleBackToLogin}
            className="px-4 py-2 bg-brand-500 text-white rounded hover:bg-brand-400"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0E0E] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-brand-400 mx-auto mb-4" />
        <p className="text-white text-lg">{status}</p>
        <p className="text-gray-400 text-sm mt-2">Please wait...</p>
        {attempts > 3 && (
          <button
            onClick={handleBackToLogin}
            className="mt-4 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
          >
            Cancel and go back
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
